import {Injectable} from '@angular/core';
import {PrintBuilderService} from './print-builder.service';
import {DataService} from './data.service';
import {firstValueFrom} from 'rxjs';
import {BsToastService} from './bs-toast-service';

/**
 * Work Order document service
 *
 * Follows the existing naming/usage pattern of invoice-document.service and shipment-document.service.
 * It delegates HTML generation to the shared PrintBuilderService but ensures the Work Order
 * layout matches the legacy work-orders-print component structure (handled inside the builder).
 */
@Injectable({providedIn: 'root'})
export class WorkorderDocumentService {
  constructor(
    private printer: PrintBuilderService,
    private data: DataService,
    private toast: BsToastService,
  ) {
  }

  /** Build the HTML used for printing a Work Order */
  buildPrintHtml(vm: any): string {
    return this.printer.buildPrintHtml(vm, 'workorder');
  }

  /** Build the HTML used for emailing a Work Order (PDF conversion done server-side) */
  buildEmailHtml(vm: any): string {
    return this.printer.buildEmailHtml(vm, 'workorder');
  }

  /** Print a Work Order directly by id from the list, modeled after invoice-form flow */
  async printById(id: string): Promise<void> {
    try {
      const vm = await this.buildVmForPrint(id);
      const html = this.buildPrintHtml(vm);
      await this.printHtmlInIframe(html);
    } catch (e) {
      console.error('Work Order print failed', e);
      this.toast.showErrorToast($localize`:Unable to print document@@unableToPrint:Unable to print document`);
    }
  }

  /**
   * Print an arbitrary HTML string using the same robust iframe flow that waits for assets.
   * Useful for callers (e.g., form component) that already have the HTML built.
   */
  public printHtml(html: string): Promise<void> {
    return this.printHtmlInIframe(html);
  }

  /** Build EmailModel payload for a Work Order id, matching invoice pattern */
  async buildEmailModelById(id: string): Promise<any> {
    try {
      const vm = await this.buildVmForPrint(id);
      const emailDocObj: any = {};
      emailDocObj.print_orientation = '1';
      emailDocObj.convert_attachment_to = 'pdf';
      emailDocObj.fromEmail = '';
      emailDocObj.fromName = 'AcctXErp';
      emailDocObj.header_id = id;
      const number = vm?.item?.wo_header?.number || '';
      emailDocObj.Subject = 'AcctXErp Work Order' + (number ? (' - ' + number) : '');
      emailDocObj.activity_message = 'Work Order';
      emailDocObj.body = 'Please see the attached Work Order' + (number ? (' - ' + number) : '') + '.<br><br><br>'
        + 'Best regards,'
        + '<br><br>'
        + 'AcctXErp'
        + '<br><br>';
      try {
        emailDocObj.pdfHtml = this.buildEmailHtml(vm);
      } catch {
        emailDocObj.pdfHtml = '';
      }
      emailDocObj.FileName = number ? `Work_Order_${number}.pdf` : 'Work_Order.pdf';
      return emailDocObj;
    } catch (e) {
      console.error('Work Order buildEmailModelById failed', e);
      this.toast.showErrorToast($localize`:Unable to prepare email@@unableToPrepareEmail:Unable to prepare email`);
      throw e;
    }
  }

  /** Load and shape VM for Work Order printing */
  private async buildVmForPrint(id: string): Promise<any> {
    const [entry, companies, billTo, shipTo, users, uoms, items] = await Promise.all([
      firstValueFrom(this.data.getData('WorkOrder', id)),
      firstValueFrom(this.data.getData('company', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r),
      firstValueFrom(this.data.getData('entity_address/addresses', '?type=bill')).then((r: any) => r?.Data ?? r).catch(() => []),
      firstValueFrom(this.data.getData('entity_address/addresses', '?type=ship')).then((r: any) => r?.Data ?? r).catch(() => []),
      firstValueFrom(this.data.getData('sys_user', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r).catch(() => []),
      firstValueFrom(this.data.getData('uom', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r).catch(() => []),
      // Load items so we can resolve item codes when wo_items lacks any code fields
      firstValueFrom(this.data.getData('item', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r).catch(() => []),
    ]);

    // Unwrap the WorkOrder payload and ensure legacy shape expected by builder
    const woRoot: any = (entry as any)?.Data ?? entry ?? {};
    const woItem: any = (woRoot && typeof woRoot === 'object' && 'item' in woRoot) ? (woRoot as any).item : woRoot;
    // Normalize arrays to avoid undefined in builder
    if (woItem && typeof woItem === 'object') {
      woItem.wo_header = woItem.wo_header ?? {};
      woItem.wo_items = Array.isArray(woItem.wo_items) ? woItem.wo_items : [];
      woItem.wo_hours = Array.isArray(woItem.wo_hours) ? woItem.wo_hours : [];
      woItem.wo_travel = Array.isArray(woItem.wo_travel) ? woItem.wo_travel : [];
      woItem.wo_notes = Array.isArray(woItem.wo_notes) ? woItem.wo_notes : [];
      woItem.wo_attachments = Array.isArray(woItem.wo_attachments) ? woItem.wo_attachments : (woItem.wo_attachments ? woItem.wo_attachments : []);
    }

    const vm: any = {
      item: woItem || {},
      company: companies,
      billToAddress: billTo,
      shipToAddress: shipTo,
      user: users,
      uom: uoms,
    };

    // Build maps of item_id -> preferred code/number and -> preferred description
    const itemCodeById: Record<string, string> = {};
    const itemDescById: Record<string, string> = {};
    // Deterministic map: item_id -> uom_id (from Items master). No alias guessing.
    const itemUomIdById: Record<string, any> = {};
    const uomCodeById: Record<string, string> = {};
    try {
      if (Array.isArray(items)) {
        for (const it of items) {
          const id = (it?.id ?? it?.item_id ?? '').toString();
          if (!id) {
            continue;
          }
          // Map the authoritative UOM id from the items master, if present
          if (it && Object.prototype.hasOwnProperty.call(it, 'uom_id')) {
            itemUomIdById[id] = it.uom_id;
          }
          const code = it?.code
            ?? it?.number
            ?? it?.item_number
            ?? it?.sku
            ?? it?.item_no
            ?? it?.part_number
            ?? it?.product_code
            ?? it?.product_number
            ?? '';
          if (code) {
            itemCodeById[id] = code;
          }
          const desc = it?.description
            ?? it?.item_description
            ?? it?.long_description
            ?? it?.inv_description
            ?? it?.product_description
            ?? it?.name
            ?? '';
          if (desc) {
            itemDescById[id] = desc;
          }
        }
      }
    } catch {
    }

    // Build map of uom_id -> uom code for backfilling when list rows lack uom_code
    try {
      if (Array.isArray(uoms)) {
        for (const u of uoms) {
          const uid = (u?.id ?? u?.uom_id ?? u?.unit_id ?? '').toString();
          if (!uid) {
            continue;
          }
          const code = u?.code ?? u?.uom_code ?? u?.name ?? '';
          if (code) {
            uomCodeById[uid] = code;
          }
        }
      }
    } catch {
    }

    // Normalize item detail rows so the builder can find code/description/uom in list-print path
    try {
      if (Array.isArray(vm?.item?.wo_items)) {
        vm.item.wo_items = vm.item.wo_items.map((it: any) => {
          const normalized: any = {...(it || {})};
          // Item code/number field aliases
          normalized.item_code = it?.item_code
            ?? it?.code
            ?? it?.number
            ?? it?.item_number
            ?? it?.inventory_item_code
            ?? it?.inventory_item_number
            ?? it?.item_no
            ?? it?.sku
            ?? it?.part_number
            ?? it?.product_code
            ?? it?.product_number
            ?? undefined;
          // If still no code, try resolve by item_id from loaded items list
          if (!normalized.item_code && (it?.item_id != null)) {
            const key = String(it.item_id);
            if (itemCodeById[key]) {
              normalized.item_code = itemCodeById[key];
            }
          }
          // Preserve any typed-in value, otherwise fall back to the resolved code above
          normalized.itemInput = it?.itemInput
            ?? it?.item_input
            ?? normalized.item_code
            ?? it?.item
            ?? it?.name
            ?? undefined;
          // Description aliases; if still missing, backfill from items master by id
          normalized.description = it?.description
            ?? it?.item_description
            ?? it?.line_description
            ?? it?.detail_description
            ?? it?.notes
            ?? it?.note
            ?? it?.comment
            ?? it?.item_desc
            ?? it?.inv_description
            ?? it?.product_description
            ?? it?.name
            ?? undefined;
          if ((normalized.description == null || normalized.description === '') && (it?.item_id != null)) {
            const key = String(it.item_id);
            if (itemDescById[key]) {
              normalized.description = itemDescById[key];
            }
          }
          // Quantity aliases (leave original if present)
          if (normalized.quantity == null) {
            normalized.quantity = it?.qty ?? it?.quantity ?? it?.amount_quantity ?? it?.quantity_ordered ?? undefined;
          }
          // Deterministic UOM backfill:
          // 1) If uom_id missing but item_id present, pull from items master map
          if (normalized.uom_id == null && it?.item_id != null) {
            const key = String(it.item_id);
            if (Object.prototype.hasOwnProperty.call(itemUomIdById, key)) {
              normalized.uom_id = itemUomIdById[key];
            }
          }
          // 2) If uom_code missing, and we have a uom_id, resolve via uomCodeById
          if (normalized.uom_code == null || normalized.uom_code === '') {
            if (typeof normalized.uom_id !== 'undefined' && normalized.uom_id !== null) {
              const code = uomCodeById[String(normalized.uom_id)];
              if (code) {
                normalized.uom_code = code;
              }
            }
          }
          return normalized;
        });
      }
    } catch {
    }

    // Provide a mapped minimal header/detail also, for any generic access
    const h = vm?.item?.wo_header || {};
    vm.item.header = {
      number: h?.number,
      header_date: h?.work_order_date || h?.required_by_date || h?.completed_date || h?.next_visit_date,
      company_id: h?.company_id,
      bill_address_id: h?.bill_address_id || h?.address_id,
      ship_address_id: h?.ship_address_id || h?.address_id,
      status: h?.status,
      assigned_user_id: h?.assigned_user_id,
      comment: h?.description || h?.dispatch_notes || '',
    };
    vm.item.detail = Array.isArray(vm?.item?.wo_items)
      ? vm.item.wo_items.map((it: any) => ({
        description: it?.description || it?.itemInput || '',
        quantity: Number(it?.quantity || 0),
        amount: Number(it?.price || it?.amount || 0),
        itemInput: it?.itemInput || '',
        control_line: false,
      })) : [];

    return vm;
  }

  private printHtmlInIframe(html: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';

      const cleanup = () => {
        setTimeout(() => {
          try {
            iframe.remove();
          } catch {
          }
        }, 400);
      };

      const triggerPrint = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
        } finally {
          cleanup();
          resolve();
        }
      };

      const waitForImages = (doc: Document, timeoutMs = 10000) => {
        try {
          const imgs = Array.from(doc.images || []);
          const pending = imgs.filter(img => !img.complete);
          if (pending.length === 0) {
            return Promise.resolve();
          }
          return new Promise<void>((res) => {
            let done = false;
            const finish = () => {
              if (!done) {
                done = true;
                res();
              }
            };
            const timer = setTimeout(finish, timeoutMs);
            let remaining = pending.length;
            const onone = () => {
              remaining -= 1;
              if (remaining <= 0) {
                clearTimeout(timer);
                finish();
              }
            };
            pending.forEach(img => {
              try {
                img.addEventListener('load', onone, {once: true});
                img.addEventListener('error', onone, {once: true});
              } catch {
                onone();
              }
            });
          });
        } catch {
          return Promise.resolve();
        }
      };

      iframe.onload = async () => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (doc) {
            await waitForImages(doc);
            // small layout settle delay
            setTimeout(triggerPrint, 50);
          } else {
            triggerPrint();
          }
        } catch {
          triggerPrint();
        }
      };

      (iframe as any).srcdoc = html;
      document.body.appendChild(iframe);
    });
  }
}
